import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { apiUrl } from "../apiUrl";

export const getRegistrosVacunasByPersonId = createAsyncThunk(
  "registroVacunas/getByPersonId",
  async (personId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${apiUrl}/api/v1/registros-vacunas/person/${personId}`
      );
      return response.data.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

export const createRegistroVacuna = createAsyncThunk(
  "registroVacunas/create",
  async (registroVacuna, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${apiUrl}/api/v1/registros-vacunas`,
        registroVacuna
      );
      return response.data.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

export const deleteRegistroVacuna = createAsyncThunk(
  "registroVacunas/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${apiUrl}/api/v1/registros-vacunas/${id}`);
      return id; // Devolvemos el ID eliminado para actualizar el estado
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

export const updateRegistroVacuna = createAsyncThunk(
  "registroVacunas/update",
  // Admite { id, changes } o { id, ...camposAActualizar }
  async (payload, { rejectWithValue }) => {
    try {
      const { patient_id, registro_id, changes, ...rest } = payload;
      const body = changes ?? rest; // si no mandas "changes", toma el resto de propiedades
      const response = await axios.patch(
        `${apiUrl}/api/v1/registros-vacunas/person/${patient_id}/registro/${registro_id}`,
        body
      );
      return response.data.data; // debe ser el registro actualizado
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

const registroVacunasSlice = createSlice({
  name: "registroVacunas",
  initialState: {
    data: [],
    isLoading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getRegistrosVacunasByPersonId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getRegistrosVacunasByPersonId.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(getRegistrosVacunasByPersonId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || {
          message: "Fallo al cargar registros de vacunas",
        };
        state.data = [];
      })

      // DELETE
      .addCase(deleteRegistroVacuna.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRegistroVacuna.fulfilled, (state, action) => {
        state.isLoading = false;
        // Filtra el elemento eliminado por ID
        state.data = state.data.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteRegistroVacuna.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || {
          message: "Fallo al eliminar el registro de vacuna",
        };
      })
      // UPDATE
      .addCase(updateRegistroVacuna.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRegistroVacuna.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload;
        const idx = state.data.findIndex((item) => item.id === updated.id);
        if (idx !== -1) {
          // mezcla por si el backend no devuelve todos los campos
          state.data[idx] = { ...state.data[idx], ...updated };
        } else {
          // si no estaba (edge case), lo agregamos
          state.data.push(updated);
        }
      })
      .addCase(updateRegistroVacuna.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || {
          message: "Fallo al actualizar el registro de vacuna",
        };
      });
  },
});

export default registroVacunasSlice.reducer;
